import { ArrowLeft, Heart } from 'lucide-react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import { getGenerationView, type GenerationView } from '../../api/index.js';
import { useWorkspace } from '../../app/runtime.js';
import { Banner, Button, Panel, StateSurface, StatusPill } from '../../design-system/index.js';
import {
  PlaybackButton,
  usePlaybackControl,
  usePlaybackTransport,
  type PlaybackItem,
} from '../../player/index.js';
import { useFavoriteActionState } from '../tracks/index.js';
import { formatDuration, formatTime, PageHeader } from '../shared/page.js';
import { DeliveryAction } from '../delivery/index.js';

type OutputView = GenerationView['outputs']['items'][number];
type DisplayInput = GenerationView['generation']['input'];
type CanonicalInput = Extract<DisplayInput, { model_id: 'v2.2' | 'v3' }>;

function isCanonicalInput(input: DisplayInput): input is CanonicalInput {
  return 'model_id' in input;
}

function isNumericRange(value: unknown): value is { minimum: number; maximum: number } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'minimum' in value &&
    typeof value.minimum === 'number' &&
    'maximum' in value &&
    typeof value.maximum === 'number'
  );
}

export function GenerationPage() {
  const { id } = useParams();
  const { connection, library } = useWorkspace();
  const player = usePlaybackTransport();
  const generation = useInfiniteQuery({
    queryKey: ['generation', id, library.id],
    initialPageParam: undefined as string | undefined,
    enabled: Boolean(id),
    queryFn: ({ pageParam }) =>
      getGenerationView(id!, {
        library_id: library.id,
        output_limit: 50,
        ...(pageParam ? { output_cursor: pageParam } : {}),
      }),
    getNextPageParam: (page) => page.outputs.next_cursor ?? undefined,
    refetchInterval: ({ state }) => {
      if (document.visibilityState !== 'visible' || !navigator.onLine) return false;
      const status = state.data?.pages[0]?.generation.status;
      return status && ['queued', 'submitting', 'submitted', 'processing'].includes(status)
        ? 3_000
        : false;
    },
  });

  if (generation.isLoading) return <StateSurface kind="loading" title="Loading generation" />;
  if (generation.error)
    return (
      <StateSurface
        kind="error"
        title="Generation unavailable"
        action={
          <Button variant="secondary" onClick={() => void generation.refetch()}>
            Try again
          </Button>
        }
      />
    );
  if (!generation.data?.pages[0])
    return (
      <StateSurface title="Generation not found">
        This request is no longer available to the current workspace.
      </StateSurface>
    );

  const { generation: request, provider_project: providerProject } = generation.data.pages[0];
  const outputs = generation.data.pages.flatMap((page) => page.outputs.items);
  const playbackItems = outputs.flatMap((item) => playbackItemFromOutput(item, library.id) ?? []);
  const playbackContext = {
    kind: 'generation' as const,
    id: `generation:${request.id}`,
    label: 'Generation outputs',
  };
  const startGenerationPlayback = async (trackId: string) => {
    const startIndex = playbackItems.findIndex((item) => item.id === trackId);
    if (startIndex < 0) return;
    await player.playQueue(playbackItems, startIndex, playbackContext);
    const completeOutputs = [...outputs];
    const seenCursors = new Set<string>();
    let cursor = generation.data?.pages.at(-1)?.outputs.next_cursor ?? null;
    try {
      while (cursor && !seenCursors.has(cursor)) {
        seenCursors.add(cursor);
        const page = await getGenerationView(request.id, {
          library_id: library.id,
          output_limit: 50,
          output_cursor: cursor,
        });
        completeOutputs.push(...page.outputs.items);
        cursor = page.outputs.next_cursor;
      }
    } catch {
      return;
    }
    const completeItems = completeOutputs.flatMap(
      (item) => playbackItemFromOutput(item, library.id) ?? [],
    );
    player.appendQueue(playbackContext.id, completeItems);
  };
  const counts = request.output_counts;
  const busy = ['queued', 'submitting', 'submitted', 'processing'].includes(request.status);
  const input = request.input;
  const canonical = isCanonicalInput(input);
  const canonicalAdvanced = canonical && input.mode === 'advanced' ? input : null;
  const canonicalV3 = canonicalAdvanced?.model_id === 'v3' ? canonicalAdvanced : null;
  const canonicalV22 = canonicalAdvanced?.model_id === 'v2.2' ? canonicalAdvanced : null;
  const legacyAdvanced = !canonical && input.mode === 'advanced' ? input : null;
  const legacyPreviewFormat =
    typeof legacyAdvanced?.preview_format_id === 'string' ? legacyAdvanced.preview_format_id : null;
  const legacyDuration = isNumericRange(legacyAdvanced?.duration_range_seconds)
    ? legacyAdvanced.duration_range_seconds
    : null;

  return (
    <div>
      <Link
        to="/create"
        className="mb-7 inline-flex min-h-11 items-center gap-2 rounded-sm text-sm text-ink-2 outline-none hover:text-ink focus-visible:ring-2 focus-visible:ring-accent-ring"
      >
        <ArrowLeft className="size-4" />
        Create
      </Link>
      <PageHeader
        title="Generation"
        action={
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill status={request.status}>
              {busy
                ? `${request.status.replaceAll('_', ' ')} · ${counts.ready}/${counts.total} ready`
                : request.status.replaceAll('_', ' ')}
            </StatusPill>
            {outputs.length ? (
              <DeliveryAction
                generationId={request.id}
                outputs={outputs.map((item) => ({
                  index: item.output.output_index,
                  title:
                    item.track.display_metadata.title ??
                    item.output.title ??
                    item.track.provider_title ??
                    `Output ${item.output.output_index + 1}`,
                }))}
              />
            ) : null}
          </div>
        }
      />

      {request.failure ? (
        <Banner className="mb-5" tone="danger" title="Generation failed">
          {request.failure.message}
          {request.failure.action_required ? ` · ${request.failure.action_required}` : ''}
        </Banner>
      ) : null}

      <Panel className="mb-5 grid gap-5 p-4 sm:p-6">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <RequestFact label="Request" value={request.id} mono />
          <RequestFact label="Requested" value={formatTime(request.requested_at)} />
          <RequestFact
            label="Project"
            value={request.composer_project.title}
            href={`/projects/${request.composer_project.id}`}
          />
          <RequestFact
            label="Provider project"
            value={providerProject?.title ?? 'Provider managed'}
          />
          <RequestFact label="Preset" value={request.preset_id ?? 'Custom'} mono />
        </div>
        <div className="border-t border-border pt-5">
          <p className="font-mono text-xs uppercase tracking-[0.12em] text-ink-3">
            Submitted input
          </p>
          <div className="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <RequestFact label="Mode" value={capitalize(input.mode)} />
            {canonical ? <RequestFact label="Model" value={input.model_id} /> : null}
            <RequestFact
              label="Composition"
              value={
                canonical
                  ? input.mode === 'advanced' && input.lyrics.mode === 'none'
                    ? 'Instrumental'
                    : 'Song'
                  : input.instrumental
                    ? 'Instrumental'
                    : 'Song'
              }
            />
            {input.mode === 'advanced' || !canonical ? (
              <RequestFact
                label="Lyrics"
                value={
                  !canonical && input.instrumental
                    ? 'None'
                    : input.mode === 'advanced' && input.lyrics.mode === 'auto'
                      ? 'Automatic'
                      : input.mode === 'advanced' && input.lyrics.mode === 'custom'
                        ? 'Custom'
                        : 'None'
                }
              />
            ) : null}
            {canonicalAdvanced ? (
              <RequestFact
                label="Style guidance"
                value={canonicalAdvanced.style_guidance.mode === 'tags' ? 'Tags' : 'Automatic'}
              />
            ) : null}
            {legacyAdvanced ? (
              <RequestFact
                label="Style guidance"
                value={legacyAdvanced.style_tags?.length ? 'Tags' : 'Automatic'}
              />
            ) : null}
            {legacyAdvanced?.prompt_strength != null ? (
              <RequestFact
                label="Prompt strength"
                value={String(legacyAdvanced.prompt_strength)}
                mono
              />
            ) : null}
            {(canonicalV3?.style_strength ?? legacyAdvanced?.style_strength) != null ? (
              <RequestFact
                label="Style strength"
                value={String(canonicalV3?.style_strength ?? legacyAdvanced?.style_strength)}
                mono
              />
            ) : null}
            {canonicalV22 ? (
              <>
                <RequestFact
                  label="Lyrics & style strength"
                  value={String(canonicalV22.lyrics_style_strength)}
                  mono
                />
                <RequestFact label="Balance" value={String(canonicalV22.balance)} mono />
                <RequestFact
                  label="BPM"
                  value={
                    canonicalV22.bpm.mode === 'manual'
                      ? String(canonicalV22.bpm.beats_per_minute)
                      : capitalize(canonicalV22.bpm.mode)
                  }
                />
                <RequestFact label="Fancy mode" value={canonicalV22.fancy_mode ? 'On' : 'Off'} />
                <RequestFact label="Boost style" value={canonicalV22.boost_style ? 'On' : 'Off'} />
              </>
            ) : null}
            {legacyPreviewFormat ? (
              <RequestFact label="Generation preview" value={legacyPreviewFormat.toUpperCase()} />
            ) : null}
            {legacyAdvanced?.reference_track_id ? (
              <RequestFact label="Reference track" value="Selected" />
            ) : null}
            {canonicalV3?.duration.mode === 'range' ? (
              <AllowedDurationFact
                minimum={canonicalV3.duration.minimum_seconds}
                maximum={canonicalV3.duration.maximum_seconds}
              />
            ) : null}
            {legacyDuration ? (
              <AllowedDurationFact
                minimum={legacyDuration.minimum}
                maximum={legacyDuration.maximum}
              />
            ) : null}
            {legacyAdvanced?.new_style != null ? (
              <RequestFact label="New style" value={legacyAdvanced.new_style ? 'On' : 'Off'} />
            ) : null}
            {legacyAdvanced?.creative_boost != null ? (
              <RequestFact
                label="Creative boost"
                value={legacyAdvanced.creative_boost ? 'On' : 'Off'}
              />
            ) : null}
            {legacyAdvanced?.comparison_mode != null ? (
              <RequestFact
                label="Compare variations"
                value={legacyAdvanced.comparison_mode ? 'On' : 'Off'}
              />
            ) : null}
          </div>
          {canonical && input.mode === 'simple' ? (
            <div className="mt-5 border-t border-border pt-5">
              <p className="font-mono text-xs uppercase tracking-[0.1em] text-ink-3">Prompt</p>
              <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-ink">{input.prompt}</p>
            </div>
          ) : null}
          {canonicalAdvanced?.style_guidance.mode === 'auto' ? (
            <TextFact
              label="Sound direction"
              value={canonicalAdvanced.style_guidance.instruction}
            />
          ) : null}
          {canonicalAdvanced?.lyrics.mode === 'auto' &&
          'instruction' in canonicalAdvanced.lyrics ? (
            <TextFact label="Lyrics idea" value={canonicalAdvanced.lyrics.instruction} />
          ) : canonicalAdvanced?.lyrics.mode === 'auto' ? (
            <TextFact
              label="Lyrics source"
              value={`${canonicalAdvanced.lyrics.source.url}${
                canonicalAdvanced.lyrics.source.lines
                  ? ` · lines ${canonicalAdvanced.lyrics.source.lines.start}–${canonicalAdvanced.lyrics.source.lines.end}`
                  : ''
              }`}
            />
          ) : null}
          {canonicalAdvanced?.lyrics.mode === 'custom' ? (
            <TextFact label="Custom lyrics" value={canonicalAdvanced.lyrics.text} />
          ) : null}
          {!canonical && input.prompt ? (
            <TextFact label="Legacy prompt" value={input.prompt} />
          ) : null}
          {!canonical && input.lyrics.mode === 'custom' ? (
            <div className="mt-5 border-t border-border pt-5">
              <p className="font-mono text-xs uppercase tracking-[0.1em] text-ink-3">
                Custom lyrics
              </p>
              <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-ink">
                {input.lyrics.text}
              </p>
            </div>
          ) : null}
          {canonicalAdvanced?.style_guidance.mode === 'tags' ||
          canonicalV3?.avoidance.mode === 'tags' ? (
            <div className="mt-5 flex flex-wrap gap-2 border-t border-border pt-5">
              {(canonicalAdvanced?.style_guidance.mode === 'tags'
                ? canonicalAdvanced.style_guidance.tags
                : []
              ).map((tag) => (
                <span
                  key={`style:${tag}`}
                  className="rounded-full border border-accent-line bg-accent-soft px-2.5 py-1 font-mono text-xs text-accent"
                >
                  {tag}
                </span>
              ))}
              {(canonicalV3?.avoidance.mode === 'tags' ? canonicalV3.avoidance.tags : []).map(
                (tag) => (
                  <span
                    key={`exclude:${tag}`}
                    className="rounded-full border border-border bg-bg px-2.5 py-1 font-mono text-xs text-ink-2"
                  >
                    exclude · {tag}
                  </span>
                ),
              )}
            </div>
          ) : null}
          {legacyAdvanced?.style_tags?.length || legacyAdvanced?.excluded_style_tags?.length ? (
            <div className="mt-5 flex flex-wrap gap-2 border-t border-border pt-5">
              {(legacyAdvanced.style_tags ?? []).map((tag) => (
                <span
                  key={`legacy-style:${tag}`}
                  className="rounded-full border border-accent-line bg-accent-soft px-2.5 py-1 font-mono text-xs text-accent"
                >
                  {tag}
                </span>
              ))}
              {(legacyAdvanced.excluded_style_tags ?? []).map((tag) => (
                <span
                  key={`legacy-exclude:${tag}`}
                  className="rounded-full border border-border bg-bg px-2.5 py-1 font-mono text-xs text-ink-2"
                >
                  exclude · {tag}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      </Panel>

      <section aria-labelledby="outputs-heading">
        <div className="mb-4 flex items-end justify-between gap-4">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.12em] text-accent">Outputs</p>
            <h2 id="outputs-heading" className="mt-2 text-2xl font-semibold tracking-[-0.03em]">
              {counts.total} {counts.total === 1 ? 'track' : 'tracks'}
            </h2>
          </div>
        </div>
        {outputs.length ? (
          <div className="grid gap-3">
            {outputs.map((item) => (
              <OutputCard
                key={item.output.id}
                item={item}
                playbackItem={playbackItemFromOutput(item, library.id)}
                onStart={() => startGenerationPlayback(item.track.id)}
                connectionActive={connection.status === 'active'}
                onSettled={() => void generation.refetch()}
              />
            ))}
          </div>
        ) : (
          <StateSurface
            kind={busy ? 'loading' : 'empty'}
            title={busy ? 'Waiting for outputs' : 'No outputs were returned'}
          >
            {busy
              ? 'The provider accepted this request; output records will appear here.'
              : 'This request completed without an output record.'}
          </StateSurface>
        )}
        {generation.hasNextPage ? (
          <div className="mt-5 flex justify-center">
            <Button
              variant="secondary"
              disabled={generation.isFetchingNextPage}
              onClick={() => void generation.fetchNextPage()}
            >
              {generation.isFetchingNextPage ? 'Loading…' : 'Load more outputs'}
            </Button>
          </div>
        ) : null}
      </section>
    </div>
  );
}

function RequestFact({
  href,
  label,
  mono,
  value,
}: {
  href?: string;
  label: string;
  mono?: boolean;
  value: string;
}) {
  const valueClass = `mt-2 block truncate text-sm text-ink ${mono ? 'font-mono text-xs' : ''}`;
  return (
    <div className="min-w-0">
      <p className="font-mono text-xs uppercase tracking-[0.1em] text-ink-3">{label}</p>
      {href ? (
        <Link
          className={`${valueClass} underline-offset-4 hover:underline`}
          to={href}
          title={value}
        >
          {value}
        </Link>
      ) : (
        <p className={valueClass} title={value}>
          {value}
        </p>
      )}
    </div>
  );
}

export function AllowedDurationFact({ maximum, minimum }: { maximum: number; minimum: number }) {
  return <RequestFact label="Allowed length" value={`${minimum}–${maximum} seconds`} />;
}

function TextFact({ label, value }: { label: string; value: string }) {
  return (
    <div className="mt-5 border-t border-border pt-5">
      <p className="font-mono text-xs uppercase tracking-[0.1em] text-ink-3">{label}</p>
      <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-ink">{value}</p>
    </div>
  );
}

function capitalize(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function playbackItemFromOutput(item: OutputView, libraryId: string): PlaybackItem | null {
  const { output, track } = item;
  const ready = output.status === 'ready' && track.status === 'ready';
  const owned = track.library_import?.status === 'available';
  if (!ready || (!track.preview.available && !owned)) return null;
  return {
    id: track.id,
    title:
      track.display_metadata.title ??
      output.title ??
      track.provider_title ??
      `Output ${output.output_index + 1}`,
    artist: track.display_metadata.artist,
    album: track.display_metadata.album,
    detailHref: `/tracks/${track.id}`,
    ...(track.generation ? { composerProjectId: track.generation.composer_project.id } : {}),
    sourcePreference: owned ? 'owned' : 'preview',
    ...(owned && track.library_import ? { libraryId, importId: track.library_import.id } : {}),
  };
}

function OutputCard({
  item,
  playbackItem,
  onStart,
  connectionActive,
  onSettled,
}: {
  item: OutputView;
  playbackItem: PlaybackItem | null;
  onStart: () => Promise<void> | void;
  connectionActive: boolean;
  onSettled: () => void;
}) {
  const output = item.output;
  const track = item.track;
  const ready = output.status === 'ready' && track.status === 'ready';
  const title =
    track.display_metadata.title ??
    output.title ??
    track.provider_title ??
    `Output ${output.output_index + 1}`;

  const favorite = useFavoriteActionState({
    favorite: track.favorite,
    onSettled,
    trackId: track.id,
  });

  const playback = usePlaybackControl(playbackItem, onStart);

  return (
    <article className="grid gap-4 rounded-lg border border-border bg-surface p-4 transition-colors hover:border-border-hi sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:items-center sm:p-5">
      <PlaybackButton
        control={playback}
        className="grid size-11 place-items-center rounded-full border border-accent-line bg-accent-soft text-accent outline-none hover:bg-accent hover:text-accent-ink focus-visible:ring-2 focus-visible:ring-accent-ring disabled:cursor-not-allowed disabled:border-border disabled:bg-bg-2 disabled:text-ink-3"
        size="icon"
        unavailableLabel={`Playback unavailable for ${title}`}
        variant="secondary"
      />
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="truncate font-medium text-ink">{title}</h3>
          <StatusPill status={output.status} />
        </div>
        <p className="mt-1 font-mono text-xs text-ink-3">
          Output {output.output_index + 1} · {formatDuration(output.duration_seconds)}
          {track.library_import
            ? ` · WAV ${track.library_import.phase ?? track.library_import.status}`
            : ''}
        </p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {track.preview.available ? <StatusPill status="ready">Preview</StatusPill> : null}
          {track.library_import ? (
            <StatusPill status={track.library_import.status}>
              Library · {track.library_import.phase ?? track.library_import.status}
            </StatusPill>
          ) : null}
          {track.delivery_summary.total > 0 ? (
            <StatusPill status={track.delivery_summary.any_succeeded ? 'succeeded' : 'pending'}>
              {track.delivery_summary.total}{' '}
              {track.delivery_summary.total === 1 ? 'delivery' : 'deliveries'}
            </StatusPill>
          ) : null}
        </div>
        {output.failure ? <p className="mt-2 text-xs text-err">{output.failure.message}</p> : null}
      </div>
      <div className="flex flex-wrap items-center gap-2 sm:justify-end">
        {ready ? (
          <Button
            aria-busy={favorite.pending || undefined}
            aria-disabled={favorite.pending || undefined}
            size="small"
            variant={favorite.displayedFavorite ? 'primary' : 'secondary'}
            disabled={!connectionActive}
            onClick={() => {
              if (!favorite.pending) void favorite.toggle();
            }}
            title={favorite.failure ? 'Favorite update failed. Try again.' : undefined}
          >
            <Heart fill={favorite.displayedFavorite ? 'currentColor' : 'none'} />
            {favorite.displayedFavorite ? 'Liked' : 'Like'}
          </Button>
        ) : null}
        <Button asChild size="small" variant="ghost">
          <Link to={`/tracks/${track.id}`}>Track detail</Link>
        </Button>
      </div>
    </article>
  );
}
